import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const ECHOTEXTURE_OPTIONS = [
  'Normal',
  'Heterogeneous',
  'Hypoechoic',
  'Hyperechoic',
  'Not Well Visualized'
];

const EPIDIDYMIS_OPTIONS = [
  'Normal',
  'Enlarged',
  'Cyst(s)',
  'Epididymitis'
];

// A testicle can be marked as surgically removed or not evaluated, which
// disables every testicle AND epididymis field for that side; the PDF then
// reports that status instead of (default "Normal") findings.
const TESTICLE_EXCLUSIONS = ['Orchiectomy', 'Not Evaluated'];

const MASS_ECHOTEXTURE = [
  'Hypoechoic',
  'Hyperechoic',
  'Heterogeneous',
  'Cystic',
  'Complex'
];

const INITIAL_SCROTAL_DATA = {
  examType: 'Scrotal',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  rightTesticle: {
    exclusion: '', // '' | 'Orchiectomy' | 'Not Evaluated'
    echotexture: 'Normal',
    length: '',
    width: '',
    height: '',
    hydrocele: false,
    varicocele: false,
    vascularity: 'Normal',
    other: '',
    masses: []
  },
  leftTesticle: {
    exclusion: '', // '' | 'Orchiectomy' | 'Not Evaluated'
    echotexture: 'Normal',
    length: '',
    width: '',
    height: '',
    hydrocele: false,
    varicocele: false,
    vascularity: 'Normal',
    other: '',
    masses: []
  },
  rightEpididymis: {
    status: 'Normal',
    other: ''
  },
  leftEpididymis: {
    status: 'Normal',
    other: ''
  },
  rightInguinalCanal: {
    evaluated: false,
    hernias: [{
      findings: 'No hernia visualized',
      containsFat: false,
      containsBowel: false,
      containsFluid: false,
      maximumSize: '',
      other: ''
    }]
  },
  leftInguinalCanal: {
    evaluated: false,
    hernias: [{
      findings: 'No hernia visualized',
      containsFat: false,
      containsBowel: false,
      containsFluid: false,
      maximumSize: '',
      other: ''
    }]
  }
};

function ScrotaForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Testicle, 1=Left Testicle
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();
  
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
      {
        ...INITIAL_SCROTAL_DATA,
        technologist: defaultTech,
      },
      'Scrotal',
      { token, facilityInfo }
    );

    useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const calculateVolume = useCallback((length, width, height) => {
    if (!length || !width || !height) return '';
    const volume = (parseFloat(length) * parseFloat(width) * parseFloat(height) * 0.52).toFixed(1);
    return volume;
  }, []);

  const handleTesticleChange = useCallback((side, field, value) => {
    const currentTesticle = formData[side];
    updateField(side, {
      ...currentTesticle,
      [field]: value
    });
  }, [formData, updateField]);

  const addMass = useCallback((side) => {
    const newMass = {
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Hypoechoic',
      description: ''
    };
    
    const currentTesticle = formData[side];
    updateField(side, {
      ...currentTesticle,
      masses: [...currentTesticle.masses, newMass]
    });
  }, [formData, updateField]);

  const removeMass = useCallback((side, index) => {
    const currentTesticle = formData[side];
    updateField(side, {
      ...currentTesticle,
      masses: currentTesticle.masses.filter((_, i) => i !== index)
    });
  }, [formData, updateField]);

  const handleMassChange = useCallback((side, index, field, value) => {
    const currentTesticle = formData[side];
    const updatedMasses = currentTesticle.masses.map((mass, i) =>
      i === index ? { ...mass, [field]: value } : mass
    );
    updateField(side, {
      ...currentTesticle,
      masses: updatedMasses
    });
  }, [formData, updateField]);

  const addHernia = useCallback((side) => {
    const newHernia = {
      findings: 'No hernia visualized',
      containsFat: false,
      containsBowel: false,
      containsFluid: false,
      maximumSize: '',
      other: ''
    };
    updateNestedField(`${side}.hernias`, [...formData[side].hernias, newHernia]);
  }, [formData, updateNestedField]);

  const removeHernia = useCallback((side, index) => {
    const newHernias = formData[side].hernias.filter((_, i) => i !== index);
    updateNestedField(`${side}.hernias`, newHernias);
  }, [formData, updateNestedField]);

  const updateHernia = useCallback((side, index, field, value) => {
    const newHernias = formData[side].hernias.map((h, i) =>
      i === index ? { ...h, [field]: value } : h
    );
    updateNestedField(`${side}.hernias`, newHernias);
  }, [formData, updateNestedField]);

  // One tab body for either side (was two near-identical copies)
  const renderTesticleTab = (side, epididymisSide, sideLabel) => {
    const testicle = formData[side];
    const epididymis = formData[epididymisSide];
    const excluded = !!testicle.exclusion;

    return (
      <div className="tab-content-grid">
        <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>{sideLabel} Testicle</h3>

        <div className="form-group" style={{ gridColumn: '1 / -1', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {TESTICLE_EXCLUSIONS.map(option => (
            <label key={option}>
              <input
                type="checkbox"
                checked={testicle.exclusion === option}
                // Mutually exclusive — checking one unchecks the other
                onChange={(e) => handleTesticleChange(side, 'exclusion', e.target.checked ? option : '')}
              />
              {' '}{option}
            </label>
          ))}
        </div>

        {excluded && (
          <p style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--color-text-secondary)' }}>
            {testicle.exclusion === 'Orchiectomy' ? 'Surgically absent' : 'Not evaluated'} — {sideLabel.toLowerCase()} testicle
            and epididymis fields are disabled and the report will say so.
          </p>
        )}

        {/* Nested grid (same class) so the existing .tab-content-grid > child
            layout rules still apply to every field inside the dimmed block */}
        <div className="tab-content-grid" style={{ gridColumn: '1 / -1', opacity: excluded ? 0.5 : 1 }}>
          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={testicle.echotexture}
              onChange={(e) => handleTesticleChange(side, 'echotexture', e.target.value)}
              disabled={excluded}
            >
              {ECHOTEXTURE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {testicle.echotexture !== 'Not Well Visualized' && (
            <>
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={testicle.length}
                  onChange={(e) => handleTesticleChange(side, 'length', e.target.value)}
                  disabled={excluded}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={testicle.width}
                  onChange={(e) => handleTesticleChange(side, 'width', e.target.value)}
                  disabled={excluded}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={testicle.height}
                  onChange={(e) => handleTesticleChange(side, 'height', e.target.value)}
                  disabled={excluded}
                />
                <span className="volume">
                  = {calculateVolume(testicle.length, testicle.width, testicle.height)} cc
                </span>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={testicle.hydrocele}
                    onChange={(e) => handleTesticleChange(side, 'hydrocele', e.target.checked)}
                    disabled={excluded}
                  />
                  Hydrocele Present
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={testicle.varicocele}
                    onChange={(e) => handleTesticleChange(side, 'varicocele', e.target.checked)}
                    disabled={excluded}
                  />
                  Varicocele Present
                </label>
              </div>

              <div className="form-group">
                <label>Vascularity (Color Doppler):</label>
                <select
                  value={testicle.vascularity}
                  onChange={(e) => handleTesticleChange(side, 'vascularity', e.target.value)}
                  disabled={excluded}
                >
                  <option value="Normal">Normal</option>
                  <option value="Increased">Increased</option>
                  <option value="Decreased">Decreased</option>
                  <option value="Absent">Absent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Additional Findings:</label>
                <input
                  type="text"
                  value={testicle.other}
                  onChange={(e) => handleTesticleChange(side, 'other', e.target.value)}
                  placeholder="Other observations"
                  disabled={excluded}
                />
              </div>

              <button type="button" onClick={() => addMass(side)} className="add-mass-btn" disabled={excluded}>
                + Add Mass/Lesion
              </button>

              {testicle.masses.map((mass, index) => (
                <div key={index} className="mass-section">
                  <h4>Mass/Lesion {index + 1}</h4>

                  <div className="size-inputs">
                    <label>Size (cm):</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="L"
                      value={mass.size1}
                      onChange={(e) => handleMassChange(side, index, 'size1', e.target.value)}
                      disabled={excluded}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="W"
                      value={mass.size2}
                      onChange={(e) => handleMassChange(side, index, 'size2', e.target.value)}
                      disabled={excluded}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="H"
                      value={mass.size3}
                      onChange={(e) => handleMassChange(side, index, 'size3', e.target.value)}
                      disabled={excluded}
                    />
                  </div>

                  <div className="form-group">
                    <label>Echotexture:</label>
                    <select
                      value={mass.echotexture}
                      onChange={(e) => handleMassChange(side, index, 'echotexture', e.target.value)}
                      disabled={excluded}
                    >
                      {MASS_ECHOTEXTURE.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      rows="2"
                      value={mass.description}
                      onChange={(e) => handleMassChange(side, index, 'description', e.target.value)}
                      placeholder="Additional details about the mass"
                      style={{ width: '100%', fontSize: '16px' }}
                      disabled={excluded}
                    />
                  </div>

                  <button type="button" onClick={() => removeMass(side, index)} className="remove-btn" disabled={excluded}>
                    Remove Mass
                  </button>
                </div>
              ))}
            </>
          )}

          <h4 style={{ marginTop: '30px' }}>{sideLabel} Epididymis</h4>
          <div className="form-group">
            <label>Status:</label>
            <select
              value={epididymis.status}
              onChange={(e) => updateNestedField(`${epididymisSide}.status`, e.target.value)}
              disabled={excluded}
            >
              {EPIDIDYMIS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={epididymis.other}
              onChange={(e) => updateNestedField(`${epididymisSide}.other`, e.target.value)}
              placeholder="Other observations"
              disabled={excluded}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderInguinalCanalTab = (side, label) => {
    const canal = formData[side];
    return (
      <div className="tab-content-grid">
        <h3 style={{ color: 'var(--color-primary)' }}>{label}</h3>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={canal.evaluated}
              onChange={(e) => updateNestedField(`${side}.evaluated`, e.target.checked)}
            />
            {' '}Evaluated
          </label>
        </div>

        <div style={{ gridColumn: '1 / -1', opacity: canal.evaluated ? 1 : 0.5, pointerEvents: canal.evaluated ? 'auto' : 'none' }}>
          {canal.hernias.map((hernia, index) => (
            <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid var(--color-border)', borderRadius: '5px', backgroundColor: 'var(--color-surface)' }}>
              {canal.hernias.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0 }}>Hernia {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeHernia(side, index)}
                    disabled={!canal.evaluated}
                    style={{ padding: '5px 10px', backgroundColor: 'var(--color-danger-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <div className="form-group">
                    <label>Findings:</label>
                    <select
                      value={hernia.findings}
                      onChange={(e) => updateHernia(side, index, 'findings', e.target.value)}
                      disabled={!canal.evaluated}
                    >
                      <option value="No hernia visualized">No hernia visualized</option>
                      <option value="Nonreducible hernia">Nonreducible hernia</option>
                      <option value="Hernia visualized only with valsalva">Hernia visualized only with valsalva</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={hernia.containsFat}
                        onChange={(e) => updateHernia(side, index, 'containsFat', e.target.checked)}
                        disabled={!canal.evaluated}
                      />
                      {' '}Contains Fat
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={hernia.containsBowel}
                        onChange={(e) => updateHernia(side, index, 'containsBowel', e.target.checked)}
                        disabled={!canal.evaluated}
                      />
                      {' '}Contains Bowel
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={hernia.containsFluid}
                        onChange={(e) => updateHernia(side, index, 'containsFluid', e.target.checked)}
                        disabled={!canal.evaluated}
                      />
                      {' '}Contains Fluid
                    </label>
                  </div>
                </div>

                <div>
                  <div className="form-group">
                    <label>Maximum Size (cm):</label>
                    <input
                      type="number"
                      step="0.1"
                      value={hernia.maximumSize}
                      onChange={(e) => updateHernia(side, index, 'maximumSize', e.target.value)}
                      placeholder="Enter size in cm"
                      disabled={!canal.evaluated}
                    />
                  </div>

                  <div className="form-group">
                    <label>Other:</label>
                    <input
                      type="text"
                      value={hernia.other}
                      onChange={(e) => updateHernia(side, index, 'other', e.target.value)}
                      placeholder="Describe other finding or additional detail"
                      disabled={!canal.evaluated}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => addHernia(side)}
            disabled={!canal.evaluated}
            style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-solid)', color: 'var(--color-input-bg)', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}
          >
            + Add Additional Hernia
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Testicle
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Testicle
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(2)}
              className={`worksheet-tab-btn${activeTab === 2 ? ' active' : ''}`}
            >
              Right Inguinal Canal
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(3)}
              className={`worksheet-tab-btn${activeTab === 3 ? ' active' : ''}`}
            >
              Left Inguinal Canal
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {activeTab === 0 && renderTesticleTab('rightTesticle', 'rightEpididymis', 'Right')}
            {activeTab === 1 && renderTesticleTab('leftTesticle', 'leftEpididymis', 'Left')}

            {/* Right Inguinal Canal Tab */}
            {activeTab === 2 && renderInguinalCanalTab('rightInguinalCanal', 'Right Inguinal Canal')}

            {/* Left Inguinal Canal Tab */}
            {activeTab === 3 && renderInguinalCanalTab('leftInguinalCanal', 'Left Inguinal Canal')}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default ScrotaForm;
